/**
 * ProductDetailModal - Full product detail popup
 *
 * Displays comprehensive product information including:
 * - Header with image, name, tier, price, weight
 * - Why/rationale section
 * - Description overview
 * - Use cases list
 * - Limitations list
 * - Materials tech specs
 * - Properties bar chart
 * - Actions: Buy, Add to Compare
 */

import { useEffect, useCallback } from 'react';
import { useProductDetail } from '../hooks/useProductDetail';
import { getProductImageUrl } from '../lib/api';
import { PropertyBarGroup } from './PropertyBar';
import { trackAffiliateClick } from '../lib/analytics';
import { getAmazonProductUrl } from '../lib/amazon';

interface ProductDetailModalProps {
  /** Product ID to display */
  productId: string | null;
  /** Whether modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Optional: Selection rationale (why) override */
  why?: string;
  /** Optional: Add to compare handler */
  onAddToCompare?: (productId: string) => void;
  /** Optional: Whether product is already in compare */
  isInCompare?: boolean;
}

function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

function getTierBadge(tier: 'budget' | 'mid' | 'premium') {
  switch (tier) {
    case 'budget':
      return (
        <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">
          Budget
        </span>
      );
    case 'mid':
      return (
        <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded">
          Mid
        </span>
      );
    case 'premium':
      return (
        <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded">
          Premium
        </span>
      );
  }
}

function ProductImagePlaceholder({ size = 'large' }: { size?: 'large' | 'small' }) {
  const sizeClasses = size === 'large' ? 'w-32 h-32' : 'w-20 h-20';
  const iconSize = size === 'large' ? 'w-12 h-12' : 'w-8 h-8';

  return (
    <div className={`${sizeClasses} flex items-center justify-center bg-neutral-800 rounded-lg shrink-0`}>
      <svg
        className={`${iconSize} text-neutral-600`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    </div>
  );
}

export function ProductDetailModal({
  productId,
  isOpen,
  onClose,
  why: whyOverride,
  onAddToCompare,
  isInCompare = false,
}: ProductDetailModalProps) {
  const { product, isLoading, isError } = useProductDetail(isOpen ? productId : null);

  // Close on escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // Loading state
  if (isLoading) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <div className="bg-neutral-900 rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !product) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <div className="bg-neutral-900 rounded-lg p-8 max-w-md">
          <h2 className="text-red-400 font-bold text-lg mb-2">Failed to load product</h2>
          <p className="text-neutral-400 mb-4">Unable to fetch product details. Please try again.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const imageUrl = getProductImageUrl(product.cf_image_id, product.image_url);
  const hasImage = imageUrl != null;
  const imageAlt = product.image_alt || product.name;
  const displayWhy = whyOverride || product.why;

  // Get primary affiliate link
  // For Amazon, ensure the affiliate tag is always present
  const { primaryLink, retailer } = (() => {
    const amazonUrl = getAmazonProductUrl(product.affiliate_links);
    if (amazonUrl) return { primaryLink: amazonUrl, retailer: 'amazon' };
    if (product.affiliate_links?.cotswold) return { primaryLink: product.affiliate_links.cotswold, retailer: 'cotswold' };
    if (product.affiliate_links?.alpkit) return { primaryLink: product.affiliate_links.alpkit, retailer: 'alpkit' };
    if (product.affiliate_links?.decathlon) return { primaryLink: product.affiliate_links.decathlon, retailer: 'decathlon' };
    return { primaryLink: null, retailer: '' };
  })();

  const handleBuyClick = () => {
    trackAffiliateClick(product.id, retailer);
  };

  const handleAddToCompare = () => {
    if (onAddToCompare && !isInCompare) {
      onAddToCompare(product.id);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-modal-title"
    >
      <div
        className="bg-neutral-900 rounded-lg border border-neutral-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 p-6">
          <div className="flex gap-4">
            {/* Image */}
            {hasImage ? (
              <img
                src={imageUrl}
                alt={imageAlt}
                className="w-32 h-32 object-cover rounded-lg shrink-0 bg-neutral-800"
              />
            ) : (
              <ProductImagePlaceholder size="large" />
            )}

            {/* Name, tier, price, weight */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-neutral-500 uppercase tracking-wider">
                  {product.category_name}
                </span>
                {getTierBadge(product.tier)}
                {product.style === 'ultralight' && (
                  <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 text-xs rounded">
                    UL
                  </span>
                )}
              </div>
              <h2
                id="product-modal-title"
                className="font-semibold text-white text-xl mb-3"
              >
                {product.name}
              </h2>
              <div className="flex items-center gap-4">
                <span className="font-mono font-bold text-2xl text-white">
                  £{product.price_gbp}
                </span>
                <span className="font-mono text-neutral-400">
                  {formatWeight(product.weight_g)}
                </span>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="shrink-0 p-2 text-neutral-400 hover:text-white transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Why Section */}
          {displayWhy && (
            <section>
              <h3 className="text-sm font-medium text-orange-400 uppercase tracking-wider mb-2">
                Why We Recommend
              </h3>
              <p className="text-neutral-300 leading-relaxed">{displayWhy}</p>
            </section>
          )}

          {/* Description */}
          {product.description && (
            <section>
              <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wider mb-2">
                Overview
              </h3>
              <p className="text-neutral-300 leading-relaxed">{product.description}</p>
            </section>
          )}

          {/* Use Cases */}
          {product.use_cases && product.use_cases.length > 0 && (
            <section>
              <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wider mb-2">
                Best For
              </h3>
              <ul className="list-disc list-inside text-neutral-300 space-y-1">
                {product.use_cases.map((useCase, i) => (
                  <li key={i}>{useCase}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Limitations */}
          {product.limitations && product.limitations.length > 0 && (
            <section>
              <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wider mb-2">
                Considerations
              </h3>
              <ul className="list-disc list-inside text-neutral-400 space-y-1">
                {product.limitations.map((limitation, i) => (
                  <li key={i}>{limitation}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Materials */}
          {product.materials && product.materials.length > 0 && (
            <section>
              <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wider mb-2">
                Materials & Tech
              </h3>
              <div className="flex flex-wrap gap-2">
                {product.materials.map((material, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-neutral-800 text-neutral-300 text-sm rounded"
                  >
                    {material}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Properties */}
          {product.properties && Object.keys(product.properties).length > 0 && (
            <section>
              <h3 className="text-sm font-medium text-neutral-400 uppercase tracking-wider mb-3">
                Performance Ratings
              </h3>
              <PropertyBarGroup properties={product.properties as Record<string, number | undefined>} />
            </section>
          )}
        </div>

        {/* Actions Footer */}
        <div className="sticky bottom-0 bg-neutral-900 border-t border-neutral-800 p-6 flex gap-3">
          {primaryLink && (
            <a
              href={primaryLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBuyClick}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-400 text-black font-bold rounded-lg transition-colors"
            >
              <span>Buy Now</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}

          {onAddToCompare && (
            <button
              onClick={handleAddToCompare}
              disabled={isInCompare}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 font-medium rounded-lg transition-colors ${
                isInCompare
                  ? 'bg-green-500/20 text-green-400 cursor-not-allowed'
                  : 'bg-neutral-800 hover:bg-neutral-700 text-white'
              }`}
            >
              {isInCompare ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                  </svg>
                  <span>In Compare</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Add to Compare</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
